import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@edp-environments/environment";
import { EdpResponse, EdpResponseBody } from "@edp-models/response.model";
import { TranslateService } from "@ngx-translate/core";
import * as crypto from "crypto-js";
import moment from "moment";
import { Observable, of, OperatorFunction, pipe, throwError, UnaryFunction } from "rxjs";
import { concatMap, delay, map, mergeMap, retryWhen } from "rxjs/operators";
import { EnvService } from "./env.service";

@Injectable({
    providedIn: "root",
})
export class ApiService {
    static readonly DEFAULT_MAX_RETRIES = 0; // maximum number of retries
    static readonly DEFAULT_BACKOFF = 1000; // increment 1 secound on each retry (do not set it to 0!)
    protected apiUrl: string;

    constructor(private http: HttpClient, private translate: TranslateService, private envService: EnvService) {
        this.apiUrl = environment.apiUrl;
    }

    /**
     * Operator to retry source Observable after a given delay
     *
     * @param delayMs Delay (in milliseconds) between each retry
     * @param maxRetries Maximum number or retries
     */
    public static addDelayedRetry<T>(delayMs: number, maxRetries: number | moment.Moment): OperatorFunction<T, T> {
        let retries = maxRetries;

        return (src: Observable<T>) =>
            src.pipe(
                retryWhen((errors: Observable<T>) =>
                    errors.pipe(
                        delay(delayMs),
                        concatMap((error: T) => {
                            let condition: boolean;
                            if (moment.isMoment(retries)) {
                                condition = moment() < retries;
                            } else {
                                condition = retries-- > 0;
                            }

                            return condition ? of(error) : throwError(error);
                        })
                    )
                )
            );
    }

    public callServicePost(service: string, args: any, replacements: [] = [], makeRetries = true): Observable<Error | EdpResponseBody> {
        const body = {
            correlationId: this.generateCorrelationId(),
            args,
            replacements,
        };

        const serviceUrl = `${this.apiUrl}${service}`;

        const obs = this.http.post<EdpResponse>(serviceUrl, body).pipe(this.addMainPOSTValidations(service));

        if (makeRetries) {
            obs.pipe(this.addRetryOnError(service));
        }

        return obs;
    }

    public callServicePostToCollection<T>(service: string, args: any, replacements: [] = [], makeRetries = true): Observable<T | Error> {
        const obs = this.callServicePost(service, args, replacements, false).pipe(this.addCollectionsPOSTValidations<T>(service));

        if (makeRetries) {
            obs.pipe(this.addRetryOnError<T>(service));
        }

        return obs;
    }

    public callServicePostCatalog(
        service: string,
        args: any,
        replacements: [] = [],
        makeRetries = true
    ): Observable<Error | EdpResponseBody> {
        const body = {
            correlationId: this.generateCorrelationId(),
            args,
            replacements,
        };

        const serviceUrl = `${environment.apiCatalogUrl}${service}`;

        const obs = this.http.post<any>(serviceUrl, body).pipe(this.addMainPOSTValidations(service));

        if (makeRetries) {
            obs.pipe(this.addRetryOnError(service));
        }

        return obs;
    }

    public callServicePostToCollectionCatalog<T>(
        service: string,
        args: any,
        replacements: [] = [],
        makeRetries = true
    ): Observable<T | Error> {
        const obs = this.callServicePostCatalog(service, args, replacements, false).pipe(this.addCollectionsPOSTValidations<T>(service));

        if (makeRetries) {
            obs.pipe(this.addRetryOnError<T>(service));
        }

        return obs;
    }

    public callServicePostV1(category: string, operation: string, body = {}) {
        const request = {
            category,
            operation,
            Body: body,
        };

        const serviceUrl = `${environment.apiUrlV1}${category}/${operation}`;

        return this.http.post<EdpResponse>(serviceUrl, request).pipe(
            map((response: EdpResponse) => {
                return response.Body;
            })
        );
    }

    private generateCorrelationId() {
        const val = crypto.SHA256().toString(crypto.enc.Hex);
        return `${val.substring(0, 8)}-${val.substring(9, 13)}-${val.substring(14, 18)}-${val.substring(19, 23)}-${val.substring(24, 36)}`;
    }

    /**
     * Add validations to a POST quind of service
     * You should catch and treat errors on effects
     *
     * @param service Service name
     */
    public addMainPOSTValidations(service: string): UnaryFunction<Observable<any>, Observable<Error | EdpResponseBody>> {
        return pipe(
            map(
                (response: EdpResponse): EdpResponseBody => {
                    if (!response.Body) {
                        throw new Error(
                            `Error when calling service "${service}"\n\
                        Unexpected response format, Body is not defined`
                        );
                    }

                    if (!response.Body.Success) {
                        let translateMessage = "pt.edp.edponline.endpoint.error";

                        if (
                            response.Body.Result &&
                            response.Body.Result.ErrorMessage &&
                            typeof response.Body.Result.ErrorMessage === "string"
                        ) {
                            translateMessage = response.Body.Result.ErrorMessage;
                        }

                        if (
                            response.Header &&
                            response.Header.Status &&
                            response.Header.Status.eCode &&
                            response.Header.Status.eCode.eDescription &&
                            typeof response.Header.Status.eCode.eDescription === "string"
                        ) {
                            translateMessage = response.Header.Status.eCode.eDescription;
                        }

                        if (
                            response.Header &&
                            response.Header.Status &&
                            response.Header.Status.eCode &&
                            response.Header.Status.eCode.eCode &&
                            response.Header.Status.eCode.eCode === "401"
                        ) {
                             window.localStorage.removeItem("token_id");
                             const logoutUrl = `${this.envService.ssoUrl()}/logout`;
                             window.location.href = environment.isApp ? `${logoutUrl}?Mobile=1` : logoutUrl;
                             translateMessage = `Error when calling service \"${service}\", 401 authorization required`;
                        }

                        throw new Error(this.translate.instant(translateMessage));
                    }

                    return response.Body;
                }
            )
        );
    }

    /**
     * Add validations to a collection quind of service
     * You should catch and treat errors on effects
     *
     * @param service Service name
     */
    public addCollectionsPOSTValidations<T>(service: string): UnaryFunction<Observable<any>, Observable<T | Error>> {
        return pipe(
            map(
                (response: EdpResponseBody): T => {
                    if (!response.Result) {
                        throw new Error(
                            `Error when calling service "${service}\"\n\
                        Unexpected service format, 'Body.Result' is not defined`
                        );
                    }

                    if (!response.Result.Collection) {
                        throw new Error(
                            `Error when calling service "${service}\"\n\
                        Unexpected service format, 'Body.Result.Collection' is not defined`
                        );
                    }

                    return response.Result.Collection;
                }
            )
        );
    }

    /**
     * Adds the retry mechanism to an existing pipeline
     *
     * @param service Service name
     * @param maxRetries Optional maximum number or retries
     * @param backoffMS Optional number of milisecounds to increment on each retrry
     */
    public addRetryOnError<T>(
        service: string,
        maxRetries = ApiService.DEFAULT_MAX_RETRIES,
        backoffMS = ApiService.DEFAULT_BACKOFF
    ): UnaryFunction<Observable<any>, Observable<T | Error>> {
        let retries = maxRetries;

        // retry mechanism
        return retryWhen((errors: Observable<any>) =>
            errors.pipe(
                mergeMap(error => {
                    if (retries-- > 0) {
                        const backoffTime = backoffMS + (maxRetries - retries) * backoffMS;
                        console.log(`Retrying service "${service}" (${maxRetries - retries} retry)...`);
                        return of(error).pipe(delay(backoffTime));
                    }

                    return throwError(error);
                })
            )
        );
    }
}
